import React, { useState, useEffect } from 'react';
import ShipmentStatusBadge from './ShipmentStatusBadge';
import { shippingService } from '../../services/shippingService';
import BaseModal from '../common/BaseModal';

const ShipmentDetailsModal = ({ shipment: shipmentProp, isOpen, onClose, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  // Lokalny stan wyświetlany w modalu - synchronizowany z propsem,
  // ale aktualizowany od razu po odświeżeniu statusu (bez czekania na reload listy).
  const [shipment, setShipment] = useState(shipmentProp);

  useEffect(() => {
    setShipment(shipmentProp);
  }, [shipmentProp]);

  if (!shipment) return null;

  const handleRefreshStatus = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const result = await shippingService.getShipmentStatus(shipment.inpost_id);
      const updated = result?.data || result;
      if (updated) {
        // Zaktualizuj dane widoczne w modalu zwróconymi wartościami
        setShipment((prev) => ({
          ...prev,
          status: updated.status ?? prev.status,
          tracking_number: updated.tracking_number ?? prev.tracking_number,
          updated_at: new Date().toISOString()
        }));
      }
      onRefresh && onRefresh();
    } catch (err) {
      setError('Błąd odświeżania statusu');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      setDownloading(true);
      setError(null);
      const blob = await shippingService.downloadLabel(shipment.inpost_id);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etykieta_${shipment.tracking_number || shipment.inpost_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Błąd pobierania etykiety');
    } finally {
      setDownloading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Czy na pewno chcesz anulować tę przesyłkę?')) return;

    try {
      setCancelling(true);
      setError(null);
      await shippingService.cancelShipment(shipment.inpost_id);
      onRefresh && onRefresh();
      onClose();
    } catch (err) {
      setError('Błąd anulowania przesyłki');
    } finally {
      setCancelling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Można dodać toast notification
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (service) => {
    const labels = {
      'inpost_locker_standard': '📦 Paczkomat',
      'inpost_courier_standard': '🚚 Kurier',
      'inpost_courier_express_1000': '⚡ Kurier Express 10:00',
      'inpost_courier_express_1200': '⚡ Kurier Express 12:00'
    };
    return labels[service] || service;
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem'
  };

  const footerContent = (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      gap: '0.5rem',
      flexWrap: 'wrap'
    }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={handleRefreshStatus}
          disabled={refreshing}
          style={{
            ...buttonStyle,
            border: `1px solid ${refreshing ? 'var(--text-muted)' : 'var(--info)'}`,
            color: refreshing ? 'var(--text-muted)' : 'var(--info)'
          }}
        >
          {refreshing ? '⏳' : '🔄'} Odśwież status
        </button>
        <button
          onClick={handleDownloadLabel}
          disabled={downloading || !shipment.tracking_number}
          style={{
            ...buttonStyle,
            border: `1px solid ${downloading ? 'var(--text-muted)' : 'var(--success)'}`,
            color: downloading ? 'var(--text-muted)' : 'var(--success)'
          }}
        >
          {downloading ? '⏳' : '🏷️'} Pobierz etykietę
        </button>
        {shipment.tracking_number && (
          <a
            href={`https://inpost.pl/sledzenie-przesylek?number=${shipment.tracking_number}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...buttonStyle,
              border: '1px solid var(--info)',
              color: 'var(--info)',
              textDecoration: 'none'
            }}
          >
            🌐 Śledź na InPost
          </a>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {shipment.status === 'created' && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{
              ...buttonStyle,
              border: `1px solid ${cancelling ? 'var(--text-muted)' : 'var(--danger)'}`,
              color: cancelling ? 'var(--text-muted)' : 'var(--danger)'
            }}
          >
            {cancelling ? '⏳' : '✕'} Anuluj
          </button>
        )}
        <button
          onClick={onClose}
          style={{
            ...buttonStyle,
            border: '1px solid var(--text-muted)',
            color: 'var(--text-muted)'
          }}
        >
          Zamknij
        </button>
      </div>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="📦 Szczegóły przesyłki"
      size="lg"
      zIndex={1050}
      footer={footerContent}
    >
      {/* Błąd */}
      {error && (
        <div style={{
          padding: '0.5rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '11px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Numer śledzenia */}
      <div style={{
        backgroundColor: 'var(--bg)',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>
          Numer śledzenia
        </div>
        <div style={{
          fontSize: '18px',
          fontWeight: '700',
          fontFamily: 'monospace',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          {shipment.tracking_number || '—'}
          {shipment.tracking_number && (
            <button
              onClick={() => copyToClipboard(shipment.tracking_number)}
              style={{
                background: 'none',
                
                cursor: 'pointer',
                fontSize: '14px'
              }}
              title="Kopiuj"
            >
              📋
            </button>
          )}
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <ShipmentStatusBadge status={shipment.status} size="large" />
        </div>
      </div>

      {/* Szczegóły */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Odbiorca */}
        <div style={{
          backgroundColor: 'var(--bg)',
          padding: '0.75rem',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            👤 Odbiorca
          </div>
          <div style={{ fontWeight: '600', fontSize: '12px' }}>
            {shipment.receiver_name || '—'}
          </div>
          {shipment.receiver_phone && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              📱 {shipment.receiver_phone}
            </div>
          )}
          {shipment.receiver_email && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              📧 {shipment.receiver_email}
            </div>
          )}
        </div>

        {/* Usługa */}
        <div style={{
          backgroundColor: 'var(--bg)',
          padding: '0.75rem',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            🚚 Usługa
          </div>
          <div style={{ fontWeight: '600', fontSize: '12px' }}>
            {getServiceLabel(shipment.service)}
          </div>
          {shipment.parcel_template && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Rozmiar: {shipment.parcel_template.toUpperCase()}
            </div>
          )}
        </div>
      </div>

      {/* Paczkomat docelowy */}
      {shipment.target_point && (
        <div style={{
          backgroundColor: 'var(--warning-light)',
          padding: '0.75rem',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          <div style={{ fontSize: '10px', color: 'var(--warning)', marginBottom: '4px' }}>
            📦 Paczkomat docelowy
          </div>
          <div style={{ fontWeight: '600', fontSize: '12px', color: 'var(--warning)' }}>
            {shipment.target_point}
          </div>
        </div>
      )}

      {/* Daty */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        marginTop: '1rem'
      }}>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            📅 Data utworzenia
          </div>
          <div style={{ fontSize: '12px' }}>
            {formatDate(shipment.created_at)}
          </div>
        </div>
        {shipment.updated_at && (
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              📅 Ostatnia aktualizacja
            </div>
            <div style={{ fontSize: '12px' }}>
              {formatDate(shipment.updated_at)}
            </div>
          </div>
        )}
      </div>

      {/* Zamówienie */}
      {shipment.order_id && (
        <div style={{
          marginTop: '1rem',
          padding: '0.5rem',
          backgroundColor: 'var(--info-light)',
          borderRadius: '4px',
          fontSize: '11px'
        }}>
          🛒 Powiązane zamówienie: <strong>#{shipment.numer_zamowienia || shipment.order_id}</strong>
        </div>
      )}
    </BaseModal>
  );
};

export default ShipmentDetailsModal;
